<template>
	<BaseModal
		data-context="REIT element modal"
		content-classes="col-10-md col-offset-1-md col-8-lg col-offset-2-lg"
		@modal-close="close"
	>
		<div class="module rounded-top">
			<div class="position-relative">
				<span class="img-lazy-wrapper aspect-ratio-4-3 aspect-ratio-16-9-md rounded-top">
					<span class="loader alpha"></span>
					<span class="alias fade rounded-top" :src="thumbnailPhoto"></span>
					<img class="rounded-top" style="opacity: 1" :src="thumbnailPhoto" alt="" role="presentation" />
				</span>
				<BaseButtonIconOnly class="modal-close modal-close--on-image" icon="close" @click="close" />
				<div v-if="completed" class="reit-element-modal__badge-positioner">
					<div class="text-transform-uppercase badge bg-color-slate-5 zeta-smo font-weight-normal">
						Completed
					</div>
				</div>
			</div>
			<div class="module-section bt-0">
				<div class="display-flex-lg align-items-center-lg justify-content-space-between-lg">
					<div class="reit-element-modal__title-container">
						<div class="heading-5 text-align-center text-align-left-lg ellipsis">
							<span>{{ name }}</span>
						</div>
						<div
							v-if="location || isAuthenticated"
							class="muted zeta-smo line-height-125 mt-25 text-align-center text-align-left-lg ellipsis"
						>
							<span v-if="location">{{ location }} </span>
							<span v-if="isAuthenticated && reitNames" v-html="reitNames"></span>
						</div>
					</div>
					<div class="display-none display-block-lg zeta">
						<BaseLink classes="button button-outline" :link="viewDetailsLink"> View&nbsp;details </BaseLink>
					</div>
				</div>
				<div v-if="strategy" class="mt-100 pt-100-lg bt-lg">
					<table class="kv kv-ruled-smo kv-ruled-mdo kv-horizontal-lg kv-horizontal-delta-values-lg">
						<tbody>
							<tr class="kv-pair col-3-lg">
								<td class="kv-key text-transform-uppercase">Strategy</td>
								<td class="kv-value heading-6">
									<span class="display-flex align-items-center justify-content-center-lg">
										<span
											class="strategy-badge strategy-badge--large mr-50"
											:style="{ 'background-color': strategyColor }"
										></span>
										<span>{{ strategy }}</span>
									</span>
								</td>
								<td class="kv-rule"></td>
							</tr>
						</tbody>
					</table>
				</div>
				<div class="display-none-lg text-align-center zeta mt-100">
					<BaseLink classes="button button-outline" :link="viewDetailsLink"> View&nbsp;details </BaseLink>
				</div>
			</div>
		</div>
	</BaseModal>
</template>

<script setup lang="ts">
import { Asset } from 'types/assets';
import { computed } from 'vue';
import { Link } from 'types/layout';
import { realEstateStrategyColor } from '@filters/shared-filters';
import { RealEstateStrategySlug } from 'types/real-estate-strategy';
import { useAppStore } from '@stores/app';

interface Props {
	assetElement: Asset;
}

interface Emits {
	(e: 'modal-close'): void;
}

const props = defineProps<Props>();

const emits = defineEmits<Emits>();

const appStore = useAppStore();

const viewDetailsLink: Link = {
	router: 'real-estate-assets',
	params: { reitElementId: props.assetElement.fundriseId },
	target: '_blank'
};

const completed = computed((): boolean => {
	return props.assetElement.status.name === 'INACTIVE';
});

const thumbnailPhoto = computed((): string => {
	return props.assetElement.thumbnail?.url ?? '';
});

const name = computed((): string => {
	return props.assetElement.name ?? props.assetElement.assetType;
});

const location = computed((): string => {
	if (
		props.assetElement.realEstateAndPrivateCreditFields?.city &&
		props.assetElement.realEstateAndPrivateCreditFields?.state
	) {
		return `${props.assetElement.realEstateAndPrivateCreditFields.city}, ${props.assetElement.realEstateAndPrivateCreditFields.state}`;
	} else {
		return '';
	}
});

const isAuthenticated = computed(() => appStore.isAuthenticated);

const reitNames = computed((): string => {
	const hasLocation = !!location.value;
	const reits = props.assetElement.fundAllocations.map((a) => a.fund.name);

	if (!reits.length) return '';

	if (reits.length > 3) {
		return `${hasLocation ? ' &bull; ' : ''}Multiple eREITs`;
	}

	const names = reits.slice(0, 3).join(', ');
	return `${hasLocation ? ' &bull; ' : ''}${names}`;
});

const strategy = computed((): string => {
	return props.assetElement.realEstateAndPrivateCreditFields?.strategy?.name ?? '';
});

const strategyColor = computed((): string => {
	return realEstateStrategyColor(
		props.assetElement.realEstateAndPrivateCreditFields?.strategy?.slug as RealEstateStrategySlug
	);
});

function close(): void {
	emits('modal-close');
}
</script>
